import { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle2, MessageCircle, Navigation } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import MapEmbed from "../components/MapEmbed";

function digitsOnly(s = "") {
  return s.replace(/[^0-9]/g, "");
}

const FALLBACK_WHATSAPP = "917200079456"; // +91 72000 79456

function directionsHref(address) {
  const q = encodeURIComponent(address || "Chennai");
  return `https://www.google.com/maps/dir/?api=1&destination=${q}`;
}

export default function Contact({ settings }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", phone: "", email: "", material: "", message: "" });
  const [status, setStatus] = useState({ loading: false, ok: false, error: "" });

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, error: "" });
    try {
      await api.post("/inquiries", form);
      setStatus({ loading: false, ok: true, error: "" });
      setForm({ name: "", phone: "", email: "", material: "", message: "" });
    } catch (err) {
      setStatus({ loading: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  const waNumber = digitsOnly(settings?.whatsapp || settings?.mobile || "") || FALLBACK_WHATSAPP;
  const sendViaWhatsapp = () => {
    const lines = [
      `*Quote request — Diamondd Traders*`,
      "",
      `Name: ${form.name || "-"}`,
      `Phone: ${form.phone || "-"}`,
      form.email ? `Email: ${form.email}` : null,
      form.material ? `Material: ${form.material}` : null,
      "",
      `Details:`,
      form.message || "-",
      "",
      `(I will share photos of the material in this chat.)`,
    ].filter(Boolean).join("\n");
    const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(lines)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-12">
        <div className="overline">{t("contact.overline")}</div>
        <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl mt-5 leading-none">
          {t("contact.titleA")} <span className="text-[#00A896]">{t("contact.titleB")}</span>
        </h1>
        <p className="text-[#8C939D] max-w-xl mt-6 leading-relaxed">{t("contact.intro")}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-7 brut-card p-8 lg:p-10" data-testid="contact-form">
          <div className="overline">{t("contact.form.formOverline")}</div>
          <h2 className="font-display font-bold uppercase tracking-tight text-3xl mt-3 text-white">{t("contact.form.title")}</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
            <div>
              <label className="overline text-[#8C939D] block mb-2">{t("contact.form.name")}</label>
              <input required value={form.name} onChange={update("name")} className="input-brut" data-testid="contact-name-input" />
            </div>
            <div>
              <label className="overline text-[#8C939D] block mb-2">{t("contact.form.phone")}</label>
              <input required value={form.phone} onChange={update("phone")} className="input-brut" data-testid="contact-phone-input" />
            </div>
            <div>
              <label className="overline text-[#8C939D] block mb-2">{t("contact.form.email")}</label>
              <input type="email" value={form.email} onChange={update("email")} className="input-brut" data-testid="contact-email-input" />
            </div>
            <div>
              <label className="overline text-[#8C939D] block mb-2">{t("contact.form.material")}</label>
              <input placeholder={t("contact.form.materialPh")} value={form.material} onChange={update("material")} className="input-brut" data-testid="contact-material-input" />
            </div>
          </div>

          <div className="mt-5">
            <label className="overline text-[#8C939D] block mb-2">{t("contact.form.message")}</label>
            <textarea required rows={5} value={form.message} onChange={update("message")} className="input-brut resize-none" placeholder={t("contact.form.messagePh")} data-testid="contact-message-input" />
          </div>

          {status.error && (
            <div className="mt-5 border border-[#DC2626] text-[#FCA5A5] p-4 text-sm" data-testid="contact-error">
              {status.error}
            </div>
          )}
          {status.ok && (
            <div className="mt-5 border border-[#16A34A] text-[#86EFAC] p-4 text-sm flex items-center gap-3" data-testid="contact-success">
              <CheckCircle2 size={18} /> {t("contact.form.success")}
            </div>
          )}

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button type="submit" disabled={status.loading} className="btn-primary disabled:opacity-60" data-testid="contact-submit-btn">
              {status.loading ? t("contact.form.sending") : <>{t("contact.form.submit")} <Send size={16} /></>}
            </button>
            {waNumber && (
              <button
                type="button"
                onClick={sendViaWhatsapp}
                className="bg-[#25D366] text-[#0A0B0E] font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center justify-center gap-2 hover:bg-[#1EBE5C] border border-[#25D366]"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle size={16} fill="currentColor" /> {t("contact.form.whatsapp")}
              </button>
            )}          </div>
        </form>

        {/* Info */}
        <div className="lg:col-span-5 grid gap-4">
          <a href={`tel:${settings?.mobile}`} className="brut-card p-6 flex items-start gap-4 hover:border-[#00A896]" data-testid="contact-info-phone">
            <Phone className="text-[#00A896] mt-1" size={22} />
            <div>
              <div className="overline text-[#8C939D]">{t("contact.info.phone")}</div>
              <div className="font-display font-bold text-xl mt-1 text-white">{settings?.mobile}</div>
            </div>
          </a>
          <a href={`mailto:${settings?.email}`} className="brut-card p-6 flex items-start gap-4 hover:border-[#00A896]" data-testid="contact-info-email">
            <Mail className="text-[#00A896] mt-1" size={22} />
            <div>
              <div className="overline text-[#8C939D]">{t("contact.info.email")}</div>
              <div className="font-display font-bold text-lg mt-1 text-white break-all">{settings?.email}</div>
            </div>
          </a>
          <div className="brut-card p-6 flex items-start gap-4" data-testid="contact-info-address">
            <MapPin className="text-[#00A896] mt-1" size={22} />
            <div className="flex-1">
              <div className="overline text-[#8C939D]">{t("contact.info.address")}</div>
              <div className="text-[#C7CBD3] mt-1 leading-relaxed text-sm">{settings?.address}</div>
              {settings?.address && (
                <a
                  href={directionsHref(settings.address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-address-directions-btn"
                  className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#00A896] border border-[#00A896] hover:bg-[#00A896] hover:text-[#0A0B0E] px-4 py-2 transition-colors"
                >
                  <Navigation size={14} /> {t("common.directions")}
                </a>
              )}
            </div>
          </div>
          <div className="p-6 border border-[#00A896] bg-[#00A896] text-[#0A0B0E]" data-testid="contact-bulk-callout">
            <div className="text-xs font-mono uppercase tracking-[0.3em]">{t("contact.bulk.overline")}</div>
            <p className="font-display font-bold text-xl mt-2 leading-tight">{t("contact.bulk.text")}</p>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <MapEmbed settings={settings} testid="contact-map" />
      </section>
    </div>
  );
}
